import {
  Badge,
  Button,
  Caption1,
  MessageBar,
  MessageBarBody,
  makeStyles,
  Spinner,
  Tooltip,
  tokens,
} from "@fluentui/react-components";
import {
  CameraRegular,
  DeleteRegular,
  ImageAddRegular,
  StarFilled,
  StarRegular,
} from "@fluentui/react-icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { catalogue, fetchProtectedImage, type ProductPhoto } from "../api/catalogue";
import { ApiError } from "../api/client";
import { motion, PHONE } from "../ui/tokens";

const useStyles = makeStyles({
  wrap: { display: "grid", gap: tokens.spacingVerticalS, minWidth: 0 },
  // Square cells that fit three across a phone and more on a laptop, so a gallery of four reads
  // as a gallery rather than a list.
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(6.5rem, 1fr))",
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  cell: {
    position: "relative",
    aspectRatio: "1",
    borderRadius: tokens.borderRadiusMedium,
    overflow: "hidden",
    backgroundColor: tokens.colorNeutralBackground3,
    minWidth: 0,
  },
  image: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  // The controls sit over the photo and appear on hover, so a gallery of four is four pictures
  // rather than four pictures and eight buttons.
  overlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: "3px",
    opacity: 0,
    background: "linear-gradient(to bottom, rgba(9,23,37,0.55), transparent 55%)",
    transitionProperty: "opacity",
    transitionDuration: motion.fast,
    ":hover": { opacity: 1 },
    ":focus-within": { opacity: 1 },
    // Nothing hovers on a phone, so the controls are simply always visible there.
    [PHONE]: { opacity: 1 },
  },
  primaryBadge: { position: "absolute", bottom: "3px", left: "3px" },
  // A button rather than a styled div: adding a photo is an action and must be reachable by
  // keyboard.
  add: {
    display: "grid",
    placeItems: "center",
    gap: "2px",
    aspectRatio: "1",
    border: `2px dashed ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground3,
    font: "inherit",
    fontSize: "11px",
    textAlign: "center",
    cursor: "pointer",
    transitionProperty: "border-color, color",
    transitionDuration: motion.fast,
    // Griffel types the `borderColor` shorthand as never — it expands to four longhands — so the
    // whole border is restated.
    ":hover": {
      border: `2px dashed ${tokens.colorCompoundBrandStroke}`,
      color: tokens.colorCompoundBrandForeground1,
    },
  },
  addIcon: { fontSize: "22px" },
  hidden: { display: "none" },
  muted: { color: tokens.colorNeutralForeground3 },
});

/** Loads one protected image and revokes its object URL when it goes away. */
function ProtectedImage({ path, alt }: { path: string; alt: string }) {
  const styles = useStyles();
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    void fetchProtectedImage(path).then((result) => {
      if (cancelled) {
        if (result) URL.revokeObjectURL(result);
        return;
      }
      objectUrl = result;
      setUrl(result);
    });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);

  return url ? <img src={url} alt={alt} className={styles.image} /> : <div />;
}

/**
 * A product's photo gallery.
 *
 * A garment needs more than one picture — the front, the neckline, the embroidery close up, the
 * care label — and the single-photo version of this made the phone form quick at the cost of
 * being no help at all to whoever had to work out what a piece was later.
 *
 * `multiple` on the file input is what does most of the work: a phone's picker lets someone select
 * four shots in one go, and `capture` is deliberately *not* set here. On the add form it is, to
 * open the camera straight away; here the pictures usually already exist in the gallery.
 */
export function PhotoManager({ productId }: { productId: string }) {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);

  const photos = useQuery({
    queryKey: ["photos", productId],
    queryFn: () => catalogue.photos(productId),
  });

  const settle = (next: ProductPhoto[]) => {
    queryClient.setQueryData(["photos", productId], next);
    void queryClient.invalidateQueries({ queryKey: ["product", productId] });
    // The list thumbnail comes from the primary photo, so stock has to be told.
    void queryClient.invalidateQueries({ queryKey: ["stock"] });
  };

  const upload = useMutation({
    mutationFn: (files: File[]) => catalogue.uploadPhotos(productId, files),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["photos", productId] });
      void queryClient.invalidateQueries({ queryKey: ["product", productId] });
      void queryClient.invalidateQueries({ queryKey: ["stock"] });
    },
  });

  const makePrimary = useMutation({
    mutationFn: (photoId: string) => catalogue.makePhotoPrimary(productId, photoId),
    onSuccess: settle,
  });

  const remove = useMutation({
    mutationFn: (photoId: string) => catalogue.deletePhoto(productId, photoId),
    onSuccess: settle,
  });

  const rows = photos.data ?? [];
  const error = upload.error ?? makePrimary.error ?? remove.error;
  const busy = upload.isPending || makePrimary.isPending || remove.isPending;

  return (
    <div className={styles.wrap}>
      {error && (
        <MessageBar intent="error">
          <MessageBarBody>
            {error instanceof ApiError || error instanceof Error
              ? error.message
              : "Could not change the photos."}
          </MessageBarBody>
        </MessageBar>
      )}

      <input
        ref={fileInput}
        className={styles.hidden}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic"
        multiple
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          if (files.length > 0) upload.mutate(files);
          // Cleared so picking the same file twice in a row still fires a change.
          event.target.value = "";
        }}
      />

      {photos.isLoading ? (
        <Spinner size="tiny" label="Loading photos" />
      ) : (
        <div className={styles.grid}>
          {rows.map((photo) => (
            <div key={photo.id} className={styles.cell}>
              <ProtectedImage path={photo.url} alt={photo.altText ?? "Product photo"} />

              <div className={styles.overlay}>
                <Tooltip
                  content={photo.isPrimary ? "Shown in lists" : "Show this one in lists"}
                  relationship="label"
                >
                  <Button
                    size="small"
                    appearance="subtle"
                    disabled={busy}
                    icon={photo.isPrimary ? <StarFilled /> : <StarRegular />}
                    onClick={() => !photo.isPrimary && makePrimary.mutate(photo.id)}
                  />
                </Tooltip>

                <Tooltip content="Remove this photo" relationship="label">
                  <Button
                    size="small"
                    appearance="subtle"
                    disabled={busy}
                    icon={<DeleteRegular />}
                    onClick={() => remove.mutate(photo.id)}
                  />
                </Tooltip>
              </div>

              {photo.isPrimary && (
                <Badge
                  className={styles.primaryBadge}
                  appearance="filled"
                  color="brand"
                  size="small"
                >
                  Main
                </Badge>
              )}
            </div>
          ))}

          <button type="button" className={styles.add} onClick={() => fileInput.current?.click()}>
            {upload.isPending ? (
              <Spinner size="tiny" />
            ) : (
              <>
                <span className={styles.addIcon}>
                  {rows.length === 0 ? <CameraRegular /> : <ImageAddRegular />}
                </span>
                {rows.length === 0 ? "Add photos" : "Add more"}
              </>
            )}
          </button>
        </div>
      )}

      <Caption1 className={styles.muted}>
        Pick several at once. The one marked <strong>Main</strong> is what shows in lists — tap the
        star on another to change it.
      </Caption1>
    </div>
  );
}
